import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { Button } from "../ui/button.js";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card.js";
import { Checkbox } from "../ui/checkbox.js";

interface PolicyText { key: string; title: string; body: string; version: number }

// Admin-editable wording (Admin → Policy Texts).
const NOTICE_KEYS = ["onboarding_data_protection_notice", "onboarding_document_retention"] as const;

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

// Shown above the Aadhaar / PAN / bank uploads in the onboarding wizard. The
// employee must tick the acknowledgement before those uploads unlock; the
// server enforces the same rule (412 from initiateUpload).
export function DataConsentNotice({ employeeId, consentedAt, onConsented }: {
  employeeId: string;
  consentedAt: string | null;
  onConsented: (at: string) => void;
}) {
  const [texts, setTexts] = useState<PolicyText[]>([]);
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(!consentedAt);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all(NOTICE_KEYS.map(k => api.get<PolicyText>(`/policy-texts/${k}`).then(r => r.data)))
      .then(setTexts)
      .catch((err) => {
        logError("DataConsentNotice.load", err);
        setLoadError("Could not load the data-protection notice. Refresh the page to try again.");
      });
  }, []);

  async function accept() {
    setSaving(true); setError("");
    try {
      const { data } = await api.post<{ dataConsentAt: string }>(`/employees/${employeeId}/onboarding/data-consent`, {});
      onConsented(data.dataConsentAt);
      setOpen(false);
    } catch (err) {
      logError("DataConsentNotice.accept", err, { employeeId });
      setError(errMsg(err) ?? "Could not record your acceptance. Please try again.");
    } finally { setSaving(false); }
  }

  const accepted = !!consentedAt;

  return (
    <Card className="mb-5 border-[var(--color-info-border)] bg-[var(--color-info-bg-subtle)]">
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="flex items-center gap-2 text-sm text-[var(--color-info-fg)]">
          <ShieldCheck size={16} /> Data protection &amp; document use
        </CardTitle>
        {accepted && (
          <Button variant="ghost" size="sm" onClick={() => setOpen(o => !o)} aria-expanded={open}>
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {open ? "Hide notice" : "View notice"}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {loadError && <p className="text-xs text-[var(--color-error-fg)]">{loadError}</p>}
        {open && texts.length > 0 && (
          <div className="max-h-64 space-y-4 overflow-y-auto rounded-md border border-surface-200 bg-surface-0 p-4">
            {texts.map(t => (
              <section key={t.key}>
                <h4 className="mb-1.5 text-[13px] font-semibold text-surface-800">{t.title}</h4>
                <p className="whitespace-pre-line text-[13px] leading-relaxed text-surface-700">{t.body}</p>
              </section>
            ))}
          </div>
        )}
        <label className="flex cursor-pointer items-start gap-2.5 text-[13px] text-surface-800 has-[:disabled]:cursor-default">
          <Checkbox
            className="mt-0.5"
            checked={accepted}
            disabled={accepted || saving || texts.length === 0}
            onCheckedChange={(v) => { if (v === true) void accept(); }}
          />
          <span>
            I have read and agree to how my documents are collected, used, stored and disposed of.
            {accepted && <span className="block text-xs text-surface-500">Accepted on {fmtDate(consentedAt)}</span>}
            {!accepted && <span className="block text-xs text-surface-500">Aadhaar, PAN and bank uploads unlock once you accept.</span>}
          </span>
        </label>
        {error && <p className="text-xs text-[var(--color-error-fg)]">{error}</p>}
      </CardContent>
    </Card>
  );
}
